/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * RaceAttachModal — one-click attach for a single-day race.
 *
 * The single-day counterpart of DailyAttachModal, and built from its parts so
 * the two read as one tool. A race takes ONE run per entrant, so a row here is
 * an ENTRANT (not a run): the run the panel would pick — the fastest that
 * covered the distance — is shown and ticked, and the row's dropdown offers
 * that entrant's other runs, or skipping them.
 *
 * WHY A MODAL. The inline panel read the race in a single request the server
 * caps at 500 rows, so a 20,000-entrant race was attached 500 at a time, one
 * click per round. Here the entrants come from the server a page at a time
 * with the race's totals, and Attach reads the whole race (500 a read) before
 * writing 500 results a request — one click for all of them.
 *
 * Same rules as the daily modal: everything starts ticked; the header box is
 * the WHOLE race, every page; search narrows the view, never the selection;
 * nothing closes the dialog while requests are in flight.
 *
 * UNDO stays, as in the inline panel: the outcome lists who was just attached
 * with a Remove each, for a wrong pick. It is read back only for attaches of
 * up to UNDO_LIMIT results — beyond that the list is too long to be the right
 * tool, and the organizer results page is.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAttachEntrantsPage, fetchAllAttachEntrants, attachMany, fetchKnownNames, titleCase,
  ENTRANTS_PAGE_SIZE,
} from './attachApi';
import { fetchTimingSources, timingSourceIdByCode, APPLICATION_TRACKED } from './resultsApi';
import { fetchAllRaceResults } from './boardApi';
import { idOf } from './boardIndexModel';
import { removeResult } from './publishApi';
import { formatClock, formatKm, formatPace } from './boardModel';
import {
  CSS, AttachProgress, SearchIcon, pageWindow, nameOf, initialsOf, numberFmt, SEARCH_DELAY_MS,
} from './DailyAttachModal';

const MUTED = '#6E6259';
const BAD = '#B42318';

/** Attaches up to this many get an undo list in the outcome. */
export const UNDO_LIMIT = 1000;

const RACE_CSS = `
.ram-run{width:100%;min-height:38px;border-radius:10px;border:1px solid rgba(36,23,8,.16);background:#fff;
  padding:0 8px;font-size:13px;color:#241708;font-variant-numeric:tabular-nums;cursor:pointer}
.dam-card{max-height:100%;overflow:auto;box-sizing:border-box}
.ram-undo{margin-top:16px;padding-top:12px;border-top:1px solid #EEE8E2}
.ram-undo ul{list-style:none;margin:8px 0 0;padding:0;max-height:180px;overflow:auto}
.ram-undo li{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:6px 0;
  border-bottom:1px solid #EEE8E2;font-size:13px}
.ram-undo button{min-height:34px;padding:0 12px;border-radius:9px;border:1px solid rgba(36,23,8,.16);
  background:#fff;font-size:12px;font-weight:700;cursor:pointer;color:#241708}
.ram-undo button.armed{background:${BAD};border-color:${BAD};color:#fff}
`;

/** "2 Nov, 6:00 am", or '' when there is no time to show. */
function whenOf(value) {
  if (!value) return '';
  const at = new Date(`${String(value).replace(' ', 'T').replace(/Z?$/, 'Z')}`);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

/** The name written onto a result: a real one, never a placeholder. */
function writtenName(entrant, knownNames) {
  return (knownNames && knownNames.get(String(entrant.athleteUserId)))
    || (entrant.athleteHandle ? titleCase(entrant.athleteHandle) : '');
}

export default function RaceAttachModal({
  api, eventActivityId, minDistanceMeters = 0, onClose,
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  // Selection = a base (every entrant / none) plus the entrants flipped from
  // it by hand, keyed by registration id with the entrant, kept across pages.
  const [allBase, setAllBase] = useState(true);
  const [flipped, setFlipped] = useState(() => new Map());
  // registration id → the run id picked instead of the fastest.
  const [picked, setPicked] = useState(() => new Map());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [removing, setRemoving] = useState(null);
  // The inline panel's "attach everybody?" question, kept: results go onto the
  // public board at once, and taking them back is one athlete at a time.
  const [confirming, setConfirming] = useState(false);
  const [raceTotals, setRaceTotals] = useState({ entrants: 0, withoutRuns: 0 });

  useEffect(() => {
    const next = search.trim();
    if (next === term) return undefined;
    const id = setTimeout(() => { setTerm(next); setPage(1); }, SEARCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [search, term]);

  const listing = useQuery({
    queryKey: ['board', 'attach-entrants', eventActivityId, minDistanceMeters, term, page],
    queryFn: async ({ signal }) => ({
      ...(await fetchAttachEntrantsPage(api, {
        eventActivityId, minDistanceMeters, page, search: term, signal,
      })),
      page,
    }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const rows = useMemo(() => (listing.data ? listing.data.rows : []), [listing.data]);

  // The race's totals, read off the page whenever it carries them.
  const fresh = listing.data && rows.length
    ? { entrants: listing.data.totalEntrants, withoutRuns: listing.data.totalWithoutRuns }
    : null;
  useEffect(() => {
    if (fresh) setRaceTotals(fresh);
  }, [fresh?.entrants, fresh?.withoutRuns]); // eslint-disable-line react-hooks/exhaustive-deps
  let totals = fresh || raceTotals;
  if (!fresh && listing.data && !term && page === 1) totals = { entrants: 0, withoutRuns: 0 };
  const totalEntrants = totals.entrants;
  const shownTotal = term ? (listing.data ? listing.data.totalMatches : 0) : totalEntrants;
  const pages = Math.max(1, Math.ceil(shownTotal / ENTRANTS_PAGE_SIZE));

  useEffect(() => {
    if (!listing.isPlaceholderData && listing.data && !rows.length && page > 1) setPage((p) => p - 1);
  }, [listing.isPlaceholderData, listing.data, rows.length, page]);

  const userIds = useMemo(
    () => [...new Set(rows.map((r) => r.athleteUserId))].sort((a, b) => a - b),
    [rows],
  );
  const names = useQuery({
    queryKey: ['board', 'known-names', userIds.join(',')],
    queryFn: ({ signal }) => fetchKnownNames(api, { userIds, signal }),
    enabled: userIds.length > 0,
  });
  const sources = useQuery({
    queryKey: ['board', 'timing-sources'],
    queryFn: ({ signal }) => fetchTimingSources(api, { signal }),
  });

  useEffect(() => {
    const before = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (before && before.focus) before.focus();
    };
  }, []);
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape' || busy) return;
      if (confirming) setConfirming(false); else onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose, confirming]);
  useEffect(() => {
    if (!busy) return undefined;
    function onLeave(e) { e.preventDefault(); e.returnValue = ''; }
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [busy]);

  const key = (entrant) => String(entrant.registrationId);
  const isTicked = (entrant) => (allBase ? !flipped.has(key(entrant)) : flipped.has(key(entrant)));
  const selected = allBase ? Math.max(0, totalEntrants - flipped.size) : flipped.size;
  const allTicked = totalEntrants > 0 && selected === totalEntrants;
  const timingSourceId = timingSourceIdByCode(sources.data || [], APPLICATION_TRACKED);

  /** The run that will be written for this entrant. */
  function runFor(entrant) {
    const choice = picked.get(key(entrant));
    if (choice) {
      const run = entrant.runs.find((r) => String(r.id) === String(choice));
      if (run) return run;
    }
    return entrant.runs[0] || null;
  }

  function setTicked(entrant, on) {
    setFlipped((was) => {
      const next = new Map(was);
      const flip = allBase ? !on : on;
      if (flip) next.set(key(entrant), entrant); else next.delete(key(entrant));
      return next;
    });
  }

  function toggle(entrant) { setTicked(entrant, !isTicked(entrant)); }

  function toggleAll() {
    setAllBase(!allTicked);
    setFlipped(new Map());
  }

  /** The dropdown: another run, or "skip" (which is unticking). */
  function choose(entrant, value) {
    if (value === '') { setTicked(entrant, false); return; }
    setPicked((was) => new Map(was).set(key(entrant), value));
    setTicked(entrant, true);
  }

  async function gatherPicks() {
    let found;
    if (!allBase) {
      found = [...flipped.values()];
    } else if (!term && totalEntrants <= rows.length) {
      // The whole race is on screen: no need to read it again.
      found = rows.filter((entrant) => !flipped.has(key(entrant)));
    } else {
      setProgress({ phase: 'gather', done: 0, total: totalEntrants });
      const everyone = await fetchAllAttachEntrants(api, {
        eventActivityId, minDistanceMeters,
        onProgress: (done) => setProgress({ phase: 'gather', done: Math.min(done, totalEntrants), total: totalEntrants }),
      });
      found = everyone.filter((entrant) => !flipped.has(key(entrant)));
    }
    const known = names.data || new Map();
    return found
      .map((entrant) => ({ entrant, run: runFor(entrant) }))
      .filter((entry) => entry.run)
      .map(({ entrant, run }) => ({
        registrationId: entrant.registrationId,
        athleteName: writtenName(entrant, known),
        run,
      }));
  }

  /** Who was just attached, with the result each became, for undo. */
  async function undoListFor(out, picks) {
    if (!out.attached.length || out.attached.length > UNDO_LIMIT) return [];
    try {
      const byRegistration = new Map();
      const fresh2 = await fetchAllRaceResults(api, { eventActivityId });
      fresh2.rows.forEach((row) => byRegistration.set(String(idOf(row.registration_id)), row));
      const nameFor = new Map(picks.map((p) => [String(p.registrationId), p.athleteName]));
      return out.attached.map((registrationId) => {
        const row = byRegistration.get(String(registrationId));
        return row ? {
          registrationId,
          resultId: row.id,
          name: nameFor.get(String(registrationId)) || row.athlete_name || 'This athlete',
        } : null;
      }).filter(Boolean);
    } catch (e) {
      // The attach went in; only the undo list is missing.
      return [];
    }
  }

  async function onAttach() {
    if (!timingSourceId) return;
    setBusy(true);
    setReport(null);
    setProgress({ phase: 'gather', done: 0, total: selected });
    try {
      const picks = await gatherPicks();
      setProgress({ phase: 'attach', done: 0, total: picks.length, attached: 0, failed: 0 });
      const out = await attachMany(api, {
        picks,
        eventActivityId,
        timingSourceId,
        onProgress: (p) => setProgress({ phase: 'attach', ...p }),
      });
      const undoable = await undoListFor(out, picks);
      setReport({ ...out, undoable, tooManyToUndo: out.attached.length > UNDO_LIMIT });
      if (!allBase) {
        setFlipped((was) => {
          const next = new Map(was);
          out.attached.forEach((id) => next.delete(String(id)));
          return next;
        });
      }
    } catch (e) {
      setReport({ attached: [], refused: [{ reason: (e && e.message) || 'Could not attach.' }], undoable: [] });
    } finally {
      setBusy(false);
      setProgress(null);
      queryClient.invalidateQueries({ queryKey: ['board'] });
    }
  }

  async function onUndo(entry) {
    setRemoving(entry.resultId);
    try {
      await removeResult(api, { resultId: entry.resultId });
      setUndoing(null);
      setReport((current) => (current ? {
        ...current,
        undoable: current.undoable.filter((e) => e.resultId !== entry.resultId),
        attached: current.attached.filter((id) => String(id) !== String(entry.registrationId)),
      } : current));
      queryClient.invalidateQueries({ queryKey: ['board'] });
    } catch (e) {
      setReport((current) => (current ? {
        ...current,
        refused: [...current.refused, { athleteName: entry.name, reason: (e && e.message) || 'Could not remove that result.' }],
      } : current));
    } finally {
      setRemoving(null);
    }
  }

  const shownPage = listing.data ? listing.data.page : page;
  const firstShown = rows.length ? (shownPage - 1) * ENTRANTS_PAGE_SIZE + 1 : 0;
  const lastShown = rows.length ? firstShown + rows.length - 1 : 0;
  const showPanel = busy || Boolean(report);

  const undoBlock = report && (report.undoable || []).length > 0 ? (
    <div className="ram-undo">
      <p style={{ margin: 0, fontWeight: 700 }}>Picked the wrong run for someone?</p>
      <p style={{ margin: '2px 0 0', fontSize: 13, color: MUTED }}>
        Remove them here and they go back in the list, with every run they recorded.
      </p>
      <ul aria-label="Just attached">
        {report.undoable.map((entry) => (
          <li key={entry.resultId}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.name}</span>
            <button
              type="button"
              className={undoing === entry.resultId ? 'armed' : ''}
              onClick={() => (undoing === entry.resultId ? onUndo(entry) : setUndoing(entry.resultId))}
              onBlur={() => setUndoing((c) => (c === entry.resultId ? null : c))}
              disabled={removing === entry.resultId}
              aria-label={undoing === entry.resultId
                ? `Confirm removing the result for ${entry.name}`
                : `Remove the result for ${entry.name}`}
            >
              {removing === entry.resultId ? 'Removing…' : (undoing === entry.resultId ? 'Confirm' : 'Remove')}
            </button>
          </li>
        ))}
      </ul>
    </div>
  ) : (report && report.tooManyToUndo ? (
    <p className="ram-undo" style={{ margin: 0, fontSize: 13, color: MUTED }}>
      To take one of these off again, remove it from the organizer results page.
    </p>
  ) : null);

  return createPortal((
    <div
      className="dam-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <style>{CSS + RACE_CSS}</style>
      <div
        ref={dialogRef}
        className="dam-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ram-title"
        aria-busy={busy}
        tabIndex={-1}
      >
        <header className="dam-head">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ minWidth: 0 }}>
              <p className="dam-kicker">Single-day race</p>
              <h2 id="ram-title" className="dam-title">Attach runs</h2>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: MUTED }}>
                Everyone without a result is ticked with their fastest run that covered the distance.
                Change a run or untick anyone to leave out — on any page.
              </p>
            </div>
            <button type="button" className="dam-x" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
          </div>

          <div className="dam-tools">
            <div className="dam-search">
              <SearchIcon />
              <label htmlFor="ram-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Search athletes by name
              </label>
              <input
                id="ram-search"
                type="search"
                value={search}
                disabled={busy}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search athletes by name"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="dam-stats">
            <span className="dam-chip" aria-label="Entrants with a run"><b>{numberFmt.format(totalEntrants)}</b> entrants with a run</span>
            {totals.withoutRuns > 0 && (
              <span className="dam-chip"><b>{numberFmt.format(totals.withoutRuns)}</b> with no run yet</span>
            )}
            <span className={`dam-chip${selected ? ' on' : ''}`}><b>{numberFmt.format(selected)}</b> selected</span>
            {term && listing.data && (
              <span className="dam-chip" aria-live="polite"><b>{numberFmt.format(listing.data.totalMatches)}</b> match “{term}”</span>
            )}
            {!allTicked && totalEntrants > 0 && !busy && (
              <button type="button" className="dam-link" onClick={() => { setAllBase(true); setFlipped(new Map()); }}>Tick all</button>
            )}
          </div>
        </header>

        <div className="dam-body" aria-busy={listing.isFetching}>
          {listing.isPending && <p className="dam-state">Looking for entrants without a result…</p>}
          {listing.isError && !listing.data && (
            <div className="dam-state">
              <p style={{ margin: '0 0 12px', color: BAD }}>{listing.error.message}</p>
              <button type="button" className="dam-btn ghost" onClick={() => listing.refetch()}>Try again</button>
            </div>
          )}
          {listing.data && !rows.length && page === 1 && (
            <p className="dam-state">
              {term
                ? `No athlete matching “${term}” is waiting for a result.`
                : 'Everybody on this race has a result, or nobody has a run we can use yet.'}
            </p>
          )}
          {rows.length > 0 && (
            <table className="dam-table" style={{ opacity: listing.isPlaceholderData ? 0.55 : 1 }}>
              <colgroup>
                <col style={{ width: 52 }} />
                <col />
                <col style={{ width: 280 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col className="dam-hide-sm" style={{ width: 100 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="dam-check" scope="col">
                    <input
                      type="checkbox"
                      aria-label="Tick every entrant in the race"
                      checked={allTicked}
                      ref={(el) => { if (el) el.indeterminate = selected > 0 && !allTicked; }}
                      onChange={toggleAll}
                      disabled={busy}
                    />
                  </th>
                  <th scope="col">Athlete</th>
                  <th scope="col">Run to attach</th>
                  <th scope="col" className="dam-num">Distance</th>
                  <th scope="col" className="dam-num">Time</th>
                  <th scope="col" className="dam-num dam-hide-sm">Pace /km</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entrant) => {
                  const on = isTicked(entrant);
                  const name = nameOf(entrant, names.data);
                  const run = runFor(entrant);
                  return (
                    <tr key={key(entrant)} className={on ? 'picked' : ''} onClick={() => { if (!busy) toggle(entrant); }}>
                      <td className="dam-check">
                        <input
                          type="checkbox"
                          aria-label={`Attach a run for ${name}`}
                          checked={on}
                          disabled={busy}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggle(entrant)}
                        />
                      </td>
                      <td>
                        <span className="dam-who">
                          <span className="dam-avatar" aria-hidden="true">{initialsOf(name)}</span>
                          <span title={name}>{name}</span>
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <label style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }} htmlFor={`ram-run-${key(entrant)}`}>
                          {`Run to attach for ${name}`}
                        </label>
                        <select
                          id={`ram-run-${key(entrant)}`}
                          className="ram-run"
                          value={on && run ? String(run.id) : ''}
                          disabled={busy}
                          onChange={(e) => choose(entrant, e.target.value)}
                        >
                          {entrant.runs.map((candidate) => (
                            <option key={candidate.id} value={String(candidate.id)}>
                              {`${formatKm(candidate.distance_meters)} · ${formatClock(candidate.duration_seconds)} · ${whenOf(candidate.started_at)}`}
                            </option>
                          ))}
                          <option value="">Skip this entrant</option>
                        </select>
                      </td>
                      <td className="dam-num">{run ? formatKm(run.distance_meters) : '—'}</td>
                      <td className="dam-num" style={{ fontWeight: 700 }}>{run ? formatClock(run.duration_seconds) : '—'}</td>
                      <td className="dam-num dam-hide-sm">{run ? formatPace(run.duration_seconds, run.distance_meters) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="dam-foot">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
              {rows.length
                ? `Showing ${numberFmt.format(firstShown)}–${numberFmt.format(lastShown)} of ${numberFmt.format(shownTotal)}${term ? ' matches' : ''}`
                : 'No entrants to show'}
            </span>
            {pages > 1 && (
              <nav className="dam-pager" aria-label="Pages">
                <button type="button" className="dam-pg" aria-label="Previous page" disabled={page <= 1 || busy} onClick={() => setPage((p) => p - 1)}>‹</button>
                {pageWindow(page, pages).map((n, i) => (n === '…'
                  ? <span key={`gap-${i}`} style={{ padding: '0 4px', color: MUTED }}>…</span>
                  : (
                    <button
                      key={n}
                      type="button"
                      className={`dam-pg${n === page ? ' cur' : ''}`}
                      aria-current={n === page ? 'page' : undefined}
                      disabled={busy}
                      onClick={() => setPage(n)}
                    >
                      {numberFmt.format(n)}
                    </button>
                  )))}
                <button type="button" className="dam-pg" aria-label="Next page" disabled={page >= pages || busy} onClick={() => setPage((p) => p + 1)}>›</button>
              </nav>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
            <button type="button" className="dam-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="dam-btn main"
              onClick={() => (selected > 1 ? setConfirming(true) : onAttach())}
              disabled={busy || selected === 0 || !timingSourceId}
            >
              {busy ? 'Attaching…' : `Attach selected (${numberFmt.format(selected)})`}
            </button>
          </div>
        </footer>

        {confirming && !showPanel && (
          <div className="dam-cover">
            <section className="dam-card" role="alertdialog" aria-labelledby="ram-confirm" aria-describedby="ram-confirm-note">
              <h3 id="ram-confirm" style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
                {`Attach ${numberFmt.format(selected)} results?`}
              </h3>
              <p id="ram-confirm-note" style={{ margin: '8px 0 0', fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
                {`This writes an official result for every ticked entrant — all ${numberFmt.format(selected)} of them, not only the ones on this page. They go onto the public board straight away, and taking one back is one athlete at a time.`}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 20 }}>
                <button type="button" className="dam-btn ghost" onClick={() => setConfirming(false)}>No, cancel</button>
                <button
                  type="button"
                  className="dam-btn main"
                  onClick={() => { setConfirming(false); onAttach(); }}
                >
                  {`Yes, attach all ${numberFmt.format(selected)}`}
                </button>
              </div>
            </section>
          </div>
        )}

        {showPanel && (
          <AttachProgress
            progress={progress}
            report={report}
            selected={selected}
            noun="result"
            onDone={() => { setReport(null); setUndoing(null); }}
          >
            {undoBlock}
          </AttachProgress>
        )}
      </div>
    </div>
  ), document.body);
}
